import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { assertUnreachableValue } from '#pplx/frontend/common/type_utils/unreachableValue.js';
import { getCompletionTextSegments } from '#pplx/frontend/composer-sdk/internal/typeahead/completionText.js';
import { MentionNodeTextContent } from '#pplx/frontend/composer-sdk/lexical/nodes/MentionNode.js';
import { HighlightedText } from '#pplx/frontend/highlighted-text/HighlightedText.js';
import classNames from 'classnames';
import React, { useEffect, useRef } from 'react';
export function TypeaheadOptionRow({ row, id, highlighted, size = 'compact', highlightStyle, onHighlight, onSelect, listboxRef, }) {
    const elementRef = useScrollHighlightedRowIntoView(highlighted, listboxRef);
    const disabled = row.item.disabled ?? false;
    const iconClassName = classNames('size-4', row.item.iconTone === 'quiet' ? 'text-secondary' : '!text-primary');
    return (_jsxs("div", { ref: elementRef, id: id, role: "option", "aria-selected": highlighted, "aria-disabled": disabled, className: classNames('flex w-full items-center rounded-lg', getTypeaheadOptionRowClassNamesBySize(size), highlighted && 'bg-subtle', disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'), 
        // Bind to mousedown, not click: mousedown fires before focus leaves the editor.
        onMouseDown: (event) => {
            // Only the primary button selects: a right/middle click must fall through to the
            // browser's native behavior (e.g. the context menu on an anchor row).
            if (event.button !== 0) {
                return;
            }
            // preventDefault stops the browser from moving focus to this row, which would
            // otherwise blur the editor and tear down the typeahead before onSelect runs.
            event.preventDefault();
            if (!disabled) {
                onSelect();
            }
        }, onMouseMove: () => {
            if (!disabled) {
                onHighlight();
            }
        }, children: [_jsxs("div", { className: "flex min-w-0 flex-1 flex-col", children: [row.item.iconLabel && _jsx("span", { className: "sr-only", children: row.item.iconLabel }), row.item.highlightQuery === undefined ? (_jsx(MentionNodeTextContent, { className: "flex w-full items-center gap-2", text: row.item.label, icon: row.item.icon, variant: "small", inlineAlignment: "middle", iconClassName: iconClassName })) : (_jsxs("span", { className: "inline-flex min-w-0 items-center gap-2", children: [row.item.icon && (_jsx(MentionNodeTextContent, { className: "shrink-0", text: "", icon: row.item.icon, variant: "small", inlineAlignment: "middle", iconClassName: iconClassName })), _jsx("span", { className: "inline-block min-w-0 truncate text-sm", children: _jsx(TypeaheadHighlightedText, { text: row.item.label, query: row.item.highlightQuery, style: highlightStyle }) })] })), row.item.subtitle && (_jsxs("span", { className: classNames('flex min-w-0 items-center gap-1.5 text-xs', row.item.icon && 'ml-6'), children: [_jsx("span", { className: "min-w-0 truncate text-tertiary", children: row.item.highlightQuery === undefined ? (row.item.subtitle) : (_jsx(TypeaheadHighlightedText, { text: row.item.subtitle, query: row.item.highlightQuery, style: highlightStyle })) }), row.item.subtitleMeta && (_jsx("span", { className: "shrink-0 whitespace-nowrap text-tertiary", children: row.item.subtitleMeta }))] }))] }), row.item.trailingAccessory ? (_jsx("span", { className: "ml-auto shrink-0 pl-3", children: row.item.trailingAccessory({ highlighted }) })) : null] }));
}
function TypeaheadHighlightedText({ text, query, style, }) {
    switch (style) {
        case 'match':
            return _jsx(HighlightedText, { text: text, searchTerm: query });
        case 'completion':
        case 'completion-subsequence':
            return getCompletionTextSegments({
                text,
                query,
                allowSubsequence: style === 'completion-subsequence',
            }).map(([input, completion], index) => (_jsx("div", { className: "inline whitespace-pre font-normal", children: input || _jsx("b", { className: "contents font-bold", children: completion }) }, index)));
        default:
            return assertUnreachableValue(style);
    }
}
function getTypeaheadOptionRowClassNamesBySize(size) {
    switch (size) {
        case 'comfortable':
            return 'gap-3 px-3 py-2';
        case 'compact':
            return 'gap-2 px-2 py-1.5';
        case 'spacious':
            return 'gap-2 px-2 py-1.5 min-h-9';
        default:
            return assertUnreachableValue(size);
    }
}
/**
 * Keeps the highlighted row visible as the user navigates. Scrolls the row into view when it
 * becomes highlighted, but only when it's entirely out of view — a partially visible row (e.g.
 * hovered at the scroll edge) is left alone to avoid a choppy snap. Returns the ref to attach
 * to the row element.
 */
function useScrollHighlightedRowIntoView(highlighted, containerRef) {
    const elementRef = useRef(null);
    useEffect(() => {
        const element = elementRef.current;
        if (highlighted && element && !isPartiallyInView(element, containerRef.current)) {
            element.scrollIntoView({ block: 'nearest' });
        }
    }, [highlighted, containerRef]);
    return elementRef;
}
// Whether the row overlaps its scroll container's viewport at all (vertically). A missing
// container is treated as out of view, preserving the plain scroll-into-view fallback.
function isPartiallyInView(element, container) {
    if (!container) {
        return false;
    }
    const elementRect = element.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();
    return elementRect.top < containerRect.bottom && elementRect.bottom > containerRect.top;
}
//# sourceMappingURL=TypeaheadOptionRow.js.map